import * as THREE from 'three'
import Materials from './Materials.js'
import Floor from './Floor.js'
import Shadows from './Shadows.js'
import Physics from './Physics.js'
import Zones from './Zones.js'
import Objects from './Objects.js'
import Car from './Car.js'
import Areas from './Areas.js'
import Tiles from './Tiles.js'
import Walls from './Walls.js'
import IntroSection from './Sections/IntroSection.js'
import ProjectsSection from './Sections/ProjectsSection.js'
import CrossroadsSection from './Sections/CrossroadsSection.js'
import InformationSection from './Sections/InformationSection.js'
import PlaygroundSection from './Sections/PlaygroundSection.js'
import Controls from './Controls.js'
import Sounds from './Sounds.js'
import { TweenLite } from 'gsap/TweenLite'
import { Power2 } from 'gsap/EasePack'
import EasterEggs from './EasterEggs.js'


export default class
{
    constructor(_options)
    {
        this.config = _options.config
        this.debug = _options.debug
        this.resources = _options.resources
        this.time = _options.time
        this.sizes = _options.sizes
        this.camera = _options.camera
        this.renderer = _options.renderer
        this.passes = _options.passes

        if(this.debug)
        {
            this.debugFolder = this.debug.addFolder('world')
            this.debugFolder.open()
        }

        this.container = new THREE.Object3D()
        this.container.matrixAutoUpdate = false

        // this.setAxes()
        this.setSounds()
        this.setControls()
        this.setFloor()
        this.setAreas()
        this.setStartingScreen()
    }

    start()
    {
        window.setTimeout(() =>
        {
            this.camera.pan.enable()
        }, 2000)

        this.setReveal()
        this.setMaterials()
        this.setShadows()
        this.setPhysics()
        this.setZones()
        this.setObjects()
        this.setCar()
        this.areas.car = this.car
        //必须按照这个顺序加载，牺牲
        this.setTiles()
        this.setWalls()
        this.setSections()
        this.setEasterEggs()
    }

    setReveal()
    {
        this.reveal = {}
        this.reveal.matcapsProgress = 0
        this.reveal.floorShadowsProgress = 0
        this.reveal.previousMatcapsProgress = null
        this.reveal.previousFloorShadowsProgress = null

        this.reveal.go = () =>
        {
            TweenLite.fromTo(this.reveal, 3, { matcapsProgress: 0 }, { matcapsProgress: 1 })
            TweenLite.fromTo(this.reveal, 3, { floorShadowsProgress: 0 }, { floorShadowsProgress: 1, delay: 0.5 })
            TweenLite.fromTo(this.shadows, 3, { alpha: 0 }, { alpha: 0.5, delay: 0.5 })

            if(this.sections.intro)
            {
                TweenLite.fromTo(this.sections.intro.instructions.arrows.label.material, 0.3, { opacity: 0 }, { opacity: 1, delay: 0.5 })
                if(this.sections.intro.otherInstructions)
                {
                    TweenLite.fromTo(this.sections.intro.otherInstructions.label.material, 0.3, { opacity: 0 }, { opacity: 1, delay: 0.75 })
                }
            }

            this.physics.car.chassis.body.sleep()
            this.physics.car.chassis.body.position.set(0, 0, 12)

            window.setTimeout(() =>
            {
                this.physics.car.chassis.body.wakeUp()
            }, 300)

            TweenLite.fromTo(this.sounds.engine.volume, 0.5, { master: 0 }, { master: 0.7, delay: 0.3, ease: Power2.easeIn })
            window.setTimeout(() =>
            {
                this.sounds.play('reveal')
            }, 400)

            if(this.controls.touch)
            {
                window.setTimeout(() =>
                {
                    this.controls.touch.reveal()
                }, 400)
            }
        }

        this.time.on('tick',() =>
        {
            if(this.reveal.matcapsProgress !== this.reveal.previousMatcapsProgress)
            {
                for(const _materialKey in this.materials.shades.items)
                {
                    const material = this.materials.shades.items[_materialKey]
                    material.uniforms.uRevealProgress.value = this.reveal.matcapsProgress
                }

                this.reveal.previousMatcapsProgress = this.reveal.matcapsProgress
            }

            if(this.reveal.floorShadowsProgress !== this.reveal.previousFloorShadowsProgress)
            {
                for(const _mesh of this.objects.floorShadows)
                {
                    _mesh.material.uniforms.uAlpha.value = this.reveal.floorShadowsProgress
                }

                this.reveal.previousFloorShadowsProgress = this.reveal.floorShadowsProgress
            }
        })

        if(this.debug)
        {
            this.debugFolder.add(this.reveal, 'matcapsProgress').step(0.0001).min(0).max(1).name('matcapsProgress')
            this.debugFolder.add(this.reveal, 'floorShadowsProgress').step(0.0001).min(0).max(1).name('floorShadowsProgress')
            this.debugFolder.add(this.reveal, 'go').name('reveal')
        }
    }

    setStartingScreen()
    {
        this.startingScreen = {}

        this.startingScreen.area = this.areas.add({
            position: new THREE.Vector2(0, 0),
            halfExtents: new THREE.Vector2(2.35, 1.5),
            hasKey: false,
            testCar: false,
            active: false
        })
        //loading
        this.startingScreen.loadingLabel = {}
        this.startingScreen.loadingLabel.geometry = new THREE.PlaneBufferGeometry(2.5, 2.5 / 4)
        this.startingScreen.loadingLabel.image = new Image()
        this.startingScreen.loadingLabel.image.src = 'data:image/png;base64,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'
        this.startingScreen.loadingLabel.texture = new THREE.Texture(this.startingScreen.loadingLabel.image)
        this.startingScreen.loadingLabel.texture.magFilter = THREE.NearestFilter
        this.startingScreen.loadingLabel.texture.minFilter = THREE.LinearFilter
        this.startingScreen.loadingLabel.texture.needsUpdate = true
        this.startingScreen.loadingLabel.material = new THREE.MeshBasicMaterial({ transparent: true, depthWrite: false, color: 0xffffff, alphaMap: this.startingScreen.loadingLabel.texture })
        this.startingScreen.loadingLabel.mesh = new THREE.Mesh(this.startingScreen.loadingLabel.geometry, this.startingScreen.loadingLabel.material)
        this.startingScreen.loadingLabel.mesh.matrixAutoUpdate = false
        this.container.add(this.startingScreen.loadingLabel.mesh)
        //加载开始
        this.startingScreen.startLabel = {}
        this.startingScreen.startLabel.geometry = new THREE.PlaneBufferGeometry(2.5, 2.5 / 4)
        this.startingScreen.startLabel.image = new Image()
        this.startingScreen.startLabel.image.src = 'data:image/png;base64,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'
        this.startingScreen.startLabel.texture = new THREE.Texture(this.startingScreen.startLabel.image)
        this.startingScreen.startLabel.texture.magFilter = THREE.NearestFilter
        this.startingScreen.startLabel.texture.minFilter = THREE.LinearFilter
        this.startingScreen.startLabel.texture.needsUpdate = true
        this.startingScreen.startLabel.material = new THREE.MeshBasicMaterial({ transparent: true, depthWrite: false, color: 0xffffff, alphaMap: this.startingScreen.startLabel.texture })
        this.startingScreen.startLabel.material.opacity = 0
        this.startingScreen.startLabel.mesh = new THREE.Mesh(this.startingScreen.startLabel.geometry, this.startingScreen.startLabel.material)
        this.startingScreen.startLabel.mesh.matrixAutoUpdate = false
        this.container.add(this.startingScreen.startLabel.mesh)

        // 进度条
        this.resources.on('progress', (_progress) =>
        {
            this.startingScreen.area.floorBorder.material.uniforms.uAlpha.value = 1
            this.startingScreen.area.floorBorder.material.uniforms.uLoadProgress.value = _progress
        })

        this.resources.on('ready', () =>
        {
            window.requestAnimationFrame(() =>
            {
                this.startingScreen.area.activate()

                TweenLite.to(this.startingScreen.area.floorBorder.material.uniforms.uAlpha, 0.3, { value: 0.3 })
                TweenLite.to(this.startingScreen.loadingLabel.material, 0.3, { opacity: 0 })
                TweenLite.to(this.startingScreen.startLabel.material, 0.3, { opacity: 1, delay: 0.3 })

            })
        })

        this.startingScreen.area.on('interact', () =>
        {
            this.startingScreen.area.deactivate()
            TweenLite.to(this.startingScreen.area.floorBorder.material.uniforms.uProgress, 0.3, { value: 0, delay: 0.4 })

            TweenLite.to(this.startingScreen.startLabel.material, 0.3, { opacity: 0, delay: 0.4 })

            this.start()

            window.setTimeout(() =>
            {
                this.reveal.go()
            }, 600)
        })
    }

    setSounds()
    {
        this.sounds = new Sounds({
            debug: this.debugFolder,
            time: this.time
        })
    }

    setAxes()
    {
        this.axis = new THREE.AxesHelper()
        this.container.add(this.axis)
    }

    setControls()
    {
        this.controls = new Controls({
            config: this.config,
            sizes: this.sizes,
            time: this.time,
            camera: this.camera,
            sounds: this.sounds
        })
    }

    setMaterials()
    {
        this.materials = new Materials({
            resources: this.resources,
            debug: this.debugFolder
        })
    }

    setFloor()
    {
        this.floor = new Floor({
            debug: this.debugFolder
        })

        this.container.add(this.floor.container)
    }

    setShadows()
    {
        this.shadows = new Shadows({
            time: this.time,
            debug: this.debugFolder,
            renderer: this.renderer,
            camera: this.camera
        })
        this.container.add(this.shadows.container)
    }

    setPhysics()
    {
        this.physics = new Physics({
            config: this.config,
            debug: this.debug,
            time: this.time,
            sizes: this.sizes,
            controls: this.controls,
            sounds: this.sounds
        })

        this.container.add(this.physics.models.container)
    }

    setZones()
    {
        this.zones = new Zones({
            time: this.time,
            physics: this.physics,
            debug: this.debugFolder
        })
        this.container.add(this.zones.container)
    }

    setAreas()
    {
        this.areas = new Areas({
            config: this.config,
            resources: this.resources,
            debug: this.debug,
            renderer: this.renderer,
            camera: this.camera,
            car: this.car,
            sounds: this.sounds,
            time: this.time
        })

        this.container.add(this.areas.container)
    }

    setTiles()
    {
        this.tiles = new Tiles({
            resources: this.resources,
            objects: this.objects,
            debug: this.debug
        })
    }

    setWalls()
    {
        this.walls = new Walls({
            resources: this.resources,
            objects: this.objects
        })
    }

    setObjects()
    {
        this.objects = new Objects({
            time: this.time,
            resources: this.resources,
            materials: this.materials,
            physics: this.physics,
            shadows: this.shadows,
            sounds: this.sounds,
            debug: this.debugFolder
        })
        this.container.add(this.objects.container)

        // window.requestAnimationFrame(() =>
        // {
        //     this.objects.merge.update()
        // })
    }

    setCar()
    {
        this.car = new Car({
            time: this.time,
            resources: this.resources,
            objects: this.objects,
            physics: this.physics,
            shadows: this.shadows,
            materials: this.materials,
            controls: this.controls,
            sounds: this.sounds,
            renderer: this.renderer,
            camera: this.camera,
            debug: this.debugFolder,
            config: this.config
        })
        this.container.add(this.car.container)
    }

    setSections()
    {
        this.sections = {}

        const options = {
            config: this.config,
            time: this.time,
            resources: this.resources,
            camera: this.camera,
            passes: this.passes,
            objects: this.objects,
            areas: this.areas,
            zones: this.zones,
            walls: this.walls,
            tiles: this.tiles,
            debug: this.debugFolder
        }

        this.sections.intro = new IntroSection({
            ...options,
            x: 0,
            y: 0
        })
        this.container.add(this.sections.intro.container)

        this.sections.crossroads = new CrossroadsSection({
            ...options,
            x: 0,
            y: - 30
        })
        this.container.add(this.sections.crossroads.container)

        this.sections.projects = new ProjectsSection({
            ...options,
            x: 30,
            y: - 30
            // x: 0,
            // y: 0
        })
        this.container.add(this.sections.projects.container)

        this.sections.information = new InformationSection({
            ...options,
            x: 1.2,
            y: - 55
            // x: 0,
            // y: - 10
        })
        this.container.add(this.sections.information.container)

        this.sections.playground = new PlaygroundSection({
            ...options,
            x: - 38,
            y: - 34
            // x: - 15,
            // y: - 4
        })
        this.container.add(this.sections.playground.container)
    }

    setEasterEggs()
    {
        this.easterEggs = new EasterEggs({
            resources: this.resources,
            car: this.car,
            walls: this.walls,
            objects: this.objects,
            materials: this.materials,
            areas: this.areas,
            config: this.config,
            physics: this.physics
        })
        this.container.add(this.easterEggs.container)
    }
}
